import { useEffect, useState } from 'react'

/**
 * Zähler für den Sprachmodus, sichtbar mit `?debug` in der Adresse.
 *
 * Entstanden, weil „es geht nicht" vom Telefon aus die einzige Auskunft war,
 * die es gab — und ich von hier aus weder das Telefon noch die Staging-Adresse
 * erreiche. Statt zu raten, was drinnen passiert, steht es jetzt auf dem
 * Bildschirm: Was das Mikrofon schickt, was ElevenLabs zurückschickt, und wo
 * die Kette reißt. Ein Foto davon sagt mehr als zwanzig Nachrichten.
 *
 * Bewusst kein Zustand im Store: Die Zähler ändern sich dreißigmal je Sekunde,
 * und das soll den Chat nicht neu zeichnen. Der Hook liest sie im Halbsekunden-
 * takt ab.
 */
export const voiceDiag = {
  /** Audioblöcke ans Netz, mit Stimme (Knopf gehalten). */
  gesendetStimme: 0,
  /** Audioblöcke ans Netz, als Stille (Knopf nicht gehalten). */
  gesendetStille: 0,
  /** Audiostücke vom Agenten. */
  empfangenAudio: 0,
  /** Transkripte des Besuchers, die ElevenLabs zurückgemeldet hat. */
  transkripte: 0,
  /** Textantworten des Agenten. */
  antworten: 0,
  /** Letzter Nachrichtentyp von ElevenLabs. */
  letzteNachricht: '—',
  /** Höchster VAD-Wert seit dem letzten Halten — sagt, ob der Server Stimme hört. */
  vadMax: 0,
  /** Knopf gerade gehalten? */
  gehalten: false,
  /** AudioContext: Zustand und Rate. */
  context: '—',
  /** Von ElevenLabs ausgehandelte Formate (Eingang → Ausgang). */
  formate: '—',
  /** Aufnahmeweg: AudioWorklet (Audio-Thread) oder ScriptProcessor (Haupt-Thread). */
  aufnahme: '—',
  /** Eigener Echofilter: an, nicht verfügbar (Rückfall) oder aus. */
  echo: '—',
  /** Letzter Schließgrund. */
  geschlossen: '—',
  /** Kamera: gesendete Bilder, Antworten, letzter Stand. */
  kameraBilder: 0,
  kameraAntworten: 0,
  kameraLetzte: '—',
  kameraStabil: 0,
  /** Wie oft die Wiedergabe neu ansetzen musste, weil das nächste Stück zu spät kam. */
  unterlaeufe: 0,
  /** Kleinster Vorlauf (ms), den die Wiedergabe beim Einplanen noch hatte. */
  vorlaufMinMs: Number.POSITIVE_INFINITY,
  /** Dazwischenreden: wie oft erkannt, wie oft ohne folgenden Satz (Fehlalarm). */
  dazwischenGezaehlt: 0,
  dazwischenFehlalarme: 0,
  /** Wie oft sie leiser wurde, weil sie etwas gehört hat (erste Stufe). */
  dazwischenVerdacht: 0,
  /** Kurze Laute, die auf ein Stichwort geprüft wurden, und das letzte Stichwort. */
  stichwortGeprueft: 0,
  stichwort: '—',
  /** Letzter Stand der Erkennung (Mikrofon, ihr Pegel, Echoanteil, Schwelle …). */
  dazwischen: null as null | {
    mic: number
    aus: number
    echo: number
    schwelle: number
    verzoegerung: number
    nachschaerfung: number
    stimmPegel: number
    stimmhaft: number | null
  },
}

export function resetVoiceDiag(): void {
  Object.assign(voiceDiag, {
    gesendetStimme: 0,
    gesendetStille: 0,
    empfangenAudio: 0,
    transkripte: 0,
    antworten: 0,
    letzteNachricht: '—',
    vadMax: 0,
    gehalten: false,
    context: '—',
    formate: '—',
    aufnahme: '—',
    echo: '—',
    geschlossen: '—',
    unterlaeufe: 0,
    vorlaufMinMs: Number.POSITIVE_INFINITY,
    kameraBilder: 0,
    kameraAntworten: 0,
    kameraLetzte: '—',
    kameraStabil: 0,
    dazwischenGezaehlt: 0,
    dazwischenFehlalarme: 0,
    dazwischenVerdacht: 0,
    stichwortGeprueft: 0,
    stichwort: '—',
    dazwischen: null,
  })
}

/** Ob die Anzeige gewünscht ist — `?debug` in der Adresse. */
export function debugGewuenscht(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('debug')
  } catch {
    return false
  }
}

/** Liefert alle 500 ms eine Kopie der Zähler. */
export function useVoiceDiagnostics(): typeof voiceDiag {
  const [stand, setStand] = useState({ ...voiceDiag })
  useEffect(() => {
    const t = setInterval(() => setStand({ ...voiceDiag }), 500)
    return () => clearInterval(t)
  }, [])
  return stand
}

/**
 * Schalter in der Adresse, nur zusammen mit `?debug` gedacht.
 *
 * `aec=1` schaltet Echounterdrückung und Rauschfilter des Mikrofons wieder
 * **ein**. Aus sind sie seit dem Versuch am Telefon, der das Knacken beendet
 * hat: iOS legt mit ihnen den gesamten Ton auf seinen Telefonie-Pfad — andere
 * Rate, eigene Nachbearbeitung, auch der Wiedergabe —, und genau dort
 * knackte es. Der Schalter bleibt, um den Vergleich jederzeit wiederholen zu
 * können.
 */
export function voiceExperiments(): { aecAn: boolean } {
  try {
    return { aecAn: new URLSearchParams(window.location.search).get('aec') === '1' }
  } catch {
    return { aecAn: false }
  }
}
